import { useState, useCallback, useRef } from 'react'
import { useParams, useNavigate } from 'react-router-dom'
import {
  Box,
  Typography,
  IconButton,
  Button,
  Alert,
  Paper,
  CircularProgress,
} from '@mui/material'
import {
  ArrowBack as ArrowBackIcon,
  Keyboard as KeyboardIcon,
  FlashlightOn as FlashlightOnIcon,
  FlashlightOff as FlashlightOffIcon,
} from '@mui/icons-material'
import { Scanner, IDetectedBarcode } from '@yudiel/react-qr-scanner'
import { useAdventure } from '../hooks/useAdventures'
import { socialApi } from '@api/endpoints/social'
import { LoadingSpinner } from '@components/common/LoadingSpinner'

export default function QRScannerPage() {
  const { id } = useParams<{ id: string }>()
  const navigate = useNavigate()
  const { data: adventure, isLoading: adventureLoading } = useAdventure(id!)

  const [torchEnabled, setTorchEnabled] = useState(false)
  const [scanError, setScanError] = useState<string | null>(null)
  const [isProcessing, setIsProcessing] = useState(false)
  const [cameraError, setCameraError] = useState<string | null>(null)
  const isProcessingRef = useRef(false)

  // Extract code from URL - handles URLs like https://example.com/?CODE or plain codes
  const extractCodeFromUrl = (rawValue: string): string => {
    try {
      // Check if it's a URL
      if (rawValue.includes('://') || rawValue.startsWith('www.')) {
        const url = new URL(rawValue.startsWith('www.') ? `https://${rawValue}` : rawValue)
        // Get everything after the ? (the search string without the leading ?)
        if (url.search) {
          return url.search.substring(1) // Remove the leading ?
        }
        // Fallback: check pathname for code
        const pathParts = url.pathname.split('/').filter(Boolean)
        if (pathParts.length > 0) {
          return pathParts[pathParts.length - 1]
        }
      }
    } catch {
      // Not a valid URL, treat as plain code
    }
    return rawValue
  }

  const handleScan = useCallback(async (detectedCodes: IDetectedBarcode[]) => {
    // Use ref for immediate synchronous check to prevent race conditions
    if (isProcessingRef.current || detectedCodes.length === 0) return

    const rawValue = detectedCodes[0].rawValue
    if (!rawValue) return

    // Extract the actual code from the URL
    const code = extractCodeFromUrl(rawValue)
    if (!code) return

    // Set ref immediately to block any subsequent rapid-fire scans
    isProcessingRef.current = true
    setIsProcessing(true)
    setScanError(null)

    try {
      const data = await socialApi.processShareCode(code)

      if (!data) {
        setScanError('No response from server.')
        isProcessingRef.current = false
        setIsProcessing(false)
        return
      }

      // Handle different response actions
      switch (data.action) {
        case 'GeocacheComplete':
          // Geocache completed successfully - don't pass sessionId as it's already done
          // The detail page will show the completed state and rating dialog
          navigate(`/adventures/${data.adventureId || id}`, {
            state: {
              geocacheComplete: true,
              justCompleted: true,
              message: data.message || `${data.adventureName} completed!`
            },
            replace: true,
          })
          break

        case 'AlreadyCompleted':
          setScanError(data.message || 'You have already completed this adventure.')
          isProcessingRef.current = false
          setIsProcessing(false)
          break

        case 'RequirementFulfilled':
          // Workflow requirement fulfilled
          navigate(`/adventures/${data.adventureId || id}`, {
            state: {
              requirementFulfilled: true,
              locationId: data.locationId,
              requirementId: data.requirementId,
              message: data.message || 'Requirement fulfilled!'
            },
            replace: true,
          })
          break

        case 'WrongLocation':
          setScanError(data.message || 'This code is valid but not for your current location.')
          isProcessingRef.current = false
          setIsProcessing(false)
          break

        case 'DiscoveryOnly':
          // Check if this code is for the current adventure
          if (data.targetId && data.targetId !== id) {
            setScanError('This code is for a different adventure.')
            isProcessingRef.current = false
            setIsProcessing(false)
          } else {
            navigate(`/adventures/${data.targetId || id}`, {
              state: {
                fromShareCode: true,
                message: data.message
              },
              replace: true,
            })
          }
          break

        case 'AuthenticationRequired':
          // Shouldn't happen on this page (user is already authenticated)
          setScanError(data.message || 'Authentication required.')
          isProcessingRef.current = false
          setIsProcessing(false)
          break

        default:
          if (data.success) {
            navigate(`/adventures/${data.adventureId || id}`, {
              state: {
                codeValidated: true,
                code: code,
                message: data.message || 'Code accepted!'
              },
              replace: true,
            })
          } else {
            setScanError(data.message || 'Invalid QR code')
            isProcessingRef.current = false
            setIsProcessing(false)
          }
          break
      }
    } catch (err: unknown) {
      const axiosError = err as { response?: { data?: { message?: string } } }
      const errorMessage = axiosError.response?.data?.message || 'Failed to validate code. Please try again.'
      setScanError(errorMessage)
      isProcessingRef.current = false
      setIsProcessing(false)
    }
  }, [id, navigate])

  const handleCameraError = useCallback((error: unknown) => {
    console.error('Camera error:', error)
    if (error instanceof Error) {
      if (error.name === 'NotAllowedError') {
        setCameraError('Camera access was denied. Please enable camera permissions in your browser settings.')
      } else if (error.name === 'NotFoundError') {
        setCameraError('No camera found. Please ensure your device has a camera.')
      } else {
        setCameraError('Unable to access camera. Please try entering the code manually.')
      }
    } else {
      setCameraError('Unable to access camera. Please try entering the code manually.')
    }
  }, [])

  const handleBack = () => {
    navigate(`/adventures/${id}`)
  }

  const handleManualEntry = () => {
    // Navigate back with a flag to open the code entry dialog
    navigate(`/adventures/${id}`, { state: { openCodeEntry: true } })
  }

  if (adventureLoading) {
    return <LoadingSpinner fullPage />
  }

  return (
    <Box
      sx={{
        height: '100vh',
        display: 'flex',
        flexDirection: 'column',
        bgcolor: 'black',
      }}
    >
      {/* Header */}
      <Box
        sx={{
          display: 'flex',
          alignItems: 'center',
          gap: 1,
          p: 2,
          bgcolor: 'rgba(0, 0, 0, 0.7)',
          color: 'white',
          zIndex: 10,
        }}
      >
        <IconButton onClick={handleBack} sx={{ color: 'white' }}>
          <ArrowBackIcon />
        </IconButton>
        <Typography variant="h6" noWrap sx={{ flex: 1, overflow: 'hidden', textOverflow: 'ellipsis' }}>
          {adventure?.title || 'Scan QR Code'}
        </Typography>
        <IconButton
          onClick={() => setTorchEnabled(!torchEnabled)}
          sx={{ color: 'white' }}
        >
          {torchEnabled ? <FlashlightOnIcon /> : <FlashlightOffIcon />}
        </IconButton>
      </Box>

      {/* Scanner Area */}
      <Box sx={{ flex: 1, position: 'relative', overflow: 'hidden' }}>
        {cameraError ? (
          <Box
            sx={{
              display: 'flex',
              flexDirection: 'column',
              alignItems: 'center',
              justifyContent: 'center',
              height: '100%',
              p: 3,
              color: 'white',
              textAlign: 'center',
            }}
          >
            <Alert severity="warning" sx={{ mb: 3, maxWidth: 400 }}>
              {cameraError}
            </Alert>
            <Button
              variant="contained"
              size="large"
              startIcon={<KeyboardIcon />}
              onClick={handleManualEntry}
            >
              Enter Code Manually
            </Button>
          </Box>
        ) : (
          <Scanner
            onScan={handleScan}
            onError={handleCameraError}
            constraints={{
              facingMode: 'environment',
            }}
            sound={false}
            components={{
              torch: torchEnabled,
            }}
            styles={{
              container: {
                width: '100%',
                height: '100%',
              },
              video: {
                width: '100%',
                height: '100%',
                objectFit: 'cover',
              },
            }}
          />
        )}

        {/* Processing overlay */}
        {isProcessing && (
          <Box
            sx={{
              position: 'absolute',
              inset: 0,
              display: 'flex',
              flexDirection: 'column',
              alignItems: 'center',
              justifyContent: 'center',
              bgcolor: 'rgba(0, 0, 0, 0.7)',
              color: 'white',
              zIndex: 20,
            }}
          >
            <CircularProgress color="inherit" size={48} sx={{ mb: 2 }} />
            <Typography>Validating code...</Typography>
          </Box>
        )}

        {/* Scan error */}
        {scanError && (
          <Box
            sx={{
              position: 'absolute',
              bottom: 100,
              left: 16,
              right: 16,
              zIndex: 20,
            }}
          >
            <Alert
              severity="error"
              onClose={() => setScanError(null)}
            >
              {scanError}
            </Alert>
          </Box>
        )}
      </Box>

      {/* Footer Instructions */}
      <Paper
        sx={{
          p: 2,
          borderRadius: 0,
          bgcolor: 'rgba(0, 0, 0, 0.9)',
          color: 'white',
        }}
        elevation={8}
      >
        <Typography variant="body2" sx={{ textAlign: 'center', mb: 2 }}>
          Point your camera at the QR code on the geocache
        </Typography>
        <Button
          variant="outlined"
          fullWidth
          startIcon={<KeyboardIcon />}
          onClick={handleManualEntry}
          sx={{
            color: 'white',
            borderColor: 'rgba(255, 255, 255, 0.5)',
            '&:hover': {
              borderColor: 'white',
              bgcolor: 'rgba(255, 255, 255, 0.1)',
            },
          }}
        >
          Enter Code Manually
        </Button>
      </Paper>
    </Box>
  )
}
